import React, { useCallback, useContext, useMemo } from 'react'

import { ItemCardVariant } from '../ItemCard'
import ItineraryItemCard from '../ItineraryItemCard'

import ProductTypeLabel from 'components/Luxkit/Label/ProductTypeLabel'
import Modal from 'components/Luxkit/Modal/Modal'
import TextLink from 'components/Luxkit/TextLink'
import SearchRentalAmenities from 'components/OfferList/OfferListTiles/SearchOfferTiles/SearchVillaTile/SearchVillaAmenities'
import VillasPageAmenitiesModalContent from 'components/OfferPage/Villas/VillasPageAmenitiesModalContent'
import Group from 'components/utils/Group'
import ModalContext from 'contexts/ModalContext'
import { sortBy, take } from 'lib/array/arrayUtils'
import TripContext from 'tripPlanner/contexts/TripContext'
import useAccommodationItemConfirmationCodes from 'tripPlanner/hooks/confirmationCodes/useAccommodationItemConfirmationCodes'
import useAccommodationItemOccupancyDescription from 'tripPlanner/hooks/occupancy/useAccommodationItemOccupancyDescription'
import { HomeAndVillasItem } from 'tripPlanner/types/tripItem'
import {
  buildMyEscapesOrderLink,
  formatShortDateRange,
  getOfferImage,
} from 'tripPlanner/utils'

interface Props {
  offer: App.VillaOffer
  item: HomeAndVillasItem
  onClickCard: (offerLink: string) => void
  onClickTripName?: () => void
  variant: ItemCardVariant
}

function HomeAndVillasBookedItemCard({
  offer,
  item,
  onClickCard,
  onClickTripName,
  variant,
}: Props) {
  const tripContext = useContext(TripContext)
  const viewAsTemplate = tripContext?.viewAsTemplate
  const canEdit = tripContext?.permissions.canEdit ?? false

  const { imgId, imgSrc } = getOfferImage(offer)

  const productTypeLabel = <ProductTypeLabel productType={offer.productType} />

  const timeDescription = formatShortDateRange(item)
  const confirmation = useAccommodationItemConfirmationCodes(item)
  const { numberOfRoomsText } = useAccommodationItemOccupancyDescription(item)

  const placeId = 'id' in item.place ? item.place.id : undefined

  const myEscapesLink = useMemo(() => {
    return canEdit && !viewAsTemplate
      ? buildMyEscapesOrderLink(item)
      : undefined
  }, [item, canEdit, viewAsTemplate])

  const onClickItineraryCard = useCallback(() => {
    if (myEscapesLink) {
      onClickCard(myEscapesLink)
    }
  }, [myEscapesLink, onClickCard])

  const amenities = useMemo(
    () =>
      sortBy(
        offer.packages[0]?.roomType?.amenities ?? [],
        (amenity) => amenity.name,
        'asc',
      ),
    [offer],
  )

  const showModal = useContext(ModalContext)
  const openAmenitiesModal = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      e.preventDefault()
      showModal(
        <Modal title="Amenities">
          <VillasPageAmenitiesModalContent amenities={amenities} />
        </Modal>,
      )
    },
    [showModal, amenities],
  )
  const amenitiesContent = useMemo(() => {
    return (
      amenities.length > 0 && (
        <Group direction="vertical" gap={4}>
          <SearchRentalAmenities amenities={take(amenities, 6)} />
          <TextLink
            weight="bold"
            underline
            size="medium"
            onClick={openAmenitiesModal}
          >
            View all {amenities.length} amenities
          </TextLink>
        </Group>
      )
    )
  }, [amenities, openAmenitiesModal])

  return (
    <ItineraryItemCard
      item={item}
      name={item.place.name}
      imgSrc={imgSrc}
      imgId={imgId}
      locationDescription={item.place.region}
      placeId={placeId}
      descriptors={[timeDescription, numberOfRoomsText]}
      address={item.place.address}
      confirmationCode={confirmation.codeString}
      confirmationCodeLabel={confirmation.label}
      imageOverlayContent={productTypeLabel}
      onClickTripName={onClickTripName}
      variant={variant}
      onClick={onClickItineraryCard}
      amenitiesContent={amenitiesContent}
    />
  )
}

export default HomeAndVillasBookedItemCard
